/**
 * The Bexio payment reconciliation control surface (`/admin/bexio/reconciliation`).
 *
 * READ-ONLY. Nothing here writes: no payment is booked, no invoice is marked paid,
 * no Bexio sync is triggered. The single button that talks to the server re-reads
 * THIS page's own data.
 *
 * ONE REQUEST. `GET /api/admin/bexio/reconciliation` takes no parameters and returns
 * the whole picture — summary, coverage, status counts, freshness, per-tenancy rows
 * with their counted documents, and the warning groups. So the page fetches once and
 * every KPI click, filter, tab and search below is a client-side narrowing of that
 * one response. A filter that re-queried would also re-run the backend's `today`,
 * which is what the due/not-due split is anchored to.
 *
 * THE BACKEND OWNS THE ARITHMETIC. The Bexio mirror is the leading source for
 * invoiced / paid / open, and the service has already decided which invoices count
 * (`pending | partial | paid` with a complete mapping quad), which are due
 * (`due_date <= today`), and what every sum is. This page displays those numbers and
 * selects rows by fields the backend set — it computes no money, no due status and no
 * day counts. There is deliberately no "Soll − Ist" anywhere: expected-vs-invoiced is
 * a separate surface with its own `direct_delta_available=false` contract, and this
 * page does not quietly become the delta view.
 *
 * WHAT IS ORG-WIDE AND WHAT IS FILTERED, since mixing the two is how a dashboard
 * starts lying: the KPI cards and the three analysis cards read the FULL response;
 * the table reads the filtered view. The headings say which is which.
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { CheckCircle2, Clock, FileText, RefreshCw, Wallet } from "lucide-react";

import { fetchBexioReconciliation } from "../../../api/adminData/bexio";
import { formatDateTime } from "../../../utils/datetime";
import {
  INVOICE_STATUS_LABEL,
  fmtMoney,
  mapLabel,
  relativeFromNow,
} from "../../../components/admin/bexio/bexioFormat";
import BexioKpiFilterCard from "../../../components/admin/bexio/workspace/BexioKpiFilterCard";
import BexioActiveFilters from "../../../components/admin/bexio/workspace/BexioActiveFilters";
import BexioFilterBar, {
  BexioFilterSelect,
} from "../../../components/admin/bexio/workspace/BexioFilterBar";
import BexioTablePagination from "../../../components/admin/bexio/workspace/BexioTablePagination";
import BexioDetailDrawer from "../../../components/admin/bexio/workspace/BexioDetailDrawer";
import {
  BexioTableEmpty,
  BexioTableError,
  BexioTableSkeleton,
} from "../../../components/admin/bexio/workspace/BexioTableStates";
// Reused from the overview rather than rebuilt: the coverage card already renders
// this exact contract (including the invalid-is-a-sub-count rule), and the warnings
// card already renders all seven groups with a per-group drilldown drawer.
import BexioMappingCoverageCard from "../../../components/admin/bexio/overview/BexioMappingCoverageCard";
import BexioWarningsCard from "../../../components/admin/bexio/overview/BexioWarningsCard";
import BexioTopOpenCard from "../../../components/admin/bexio/reconciliation/BexioTopOpenCard";
import BexioStatusMixCard from "../../../components/admin/bexio/reconciliation/BexioStatusMixCard";
import BexioTenancyTable from "../../../components/admin/bexio/reconciliation/BexioTenancyTable";
import BexioCountedDocumentsTable from "../../../components/admin/bexio/reconciliation/BexioCountedDocumentsTable";
import BexioReconciliationDrawerBody from "../../../components/admin/bexio/reconciliation/BexioReconciliationDrawerBody";
import {
  COUNTED_STATUSES,
  KPI_ROW_FILTERS,
  countedDocumentRows,
  coveragePercent,
  filterTenancyRows,
  flaggedTenancyIds,
  rowHasHint,
} from "../../../components/admin/bexio/reconciliation/reconciliationView";

const DASH = "—";

const EMPTY_FILTERS = { q: "", status: "", hint: "", kpi: "all" };

/** Which KPI card each filter state corresponds to; `null` = none of them. */
function activeKpi(filters) {
  return KPI_ROW_FILTERS[filters.kpi] ? filters.kpi : null;
}

const STATUS_OPTIONS = [
  { value: "", label: "Bexio Status: Alle" },
  ...COUNTED_STATUSES.map((s) => ({
    value: s,
    label: mapLabel(INVOICE_STATUS_LABEL, s),
  })),
];

const HINT_OPTIONS = [
  { value: "", label: "Hinweise: Alle" },
  { value: "with", label: "Nur mit Hinweis" },
  { value: "without", label: "Ohne Hinweis" },
];

const TABS = [
  { key: "tenancies", label: "Nach Mietverhältnis" },
  { key: "documents", label: "Berücksichtigte Rechnungen" },
];

/**
 * The row noun in the three grammatical cases the shared components need.
 *
 * The pagination renders "von N …" (dative plural) while the empty state renders
 * "Keine … vorhanden" (nominative plural). For "Rechnungen" and "Kontakte" those
 * forms coincide, which is why one string sufficed on the sibling pages;
 * "Mietverhältnis" declines, so each position gets its own form instead of a
 * regex trimming an "n" off the end.
 */
const NOUNS = {
  tenancies: {
    one: "Mietverhältnis",
    many: "Mietverhältnisse",
    dative: "Mietverhältnissen",
  },
  documents: { one: "Rechnung", many: "Rechnungen", dative: "Rechnungen" },
};

const KPI_CHIP_LABEL = {
  open_due: "Offen (fällig)",
  open_not_yet_due: "Offen (nicht fällig)",
  paid: "Bezahlt",
};

export default function BexioReconciliationPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [tab, setTab] = useState("tenancies");
  const [limit, setLimit] = useState(25);
  const [offset, setOffset] = useState(0);
  /** The tenancy whose drawer is open. A document click opens its parent tenancy. */
  const [selected, setSelected] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    setError(null);
    return fetchBexioReconciliation()
      .then((res) => {
        setData(res);
        setError(null);
      })
      .catch((e) => setError(e?.message || "Abgleich konnte nicht geladen werden."))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const summary = data?.summary;
  const currency = summary?.currency || "CHF";
  const coverage = data?.mapping_coverage;
  const allTenancies = useMemo(
    () => (Array.isArray(data?.tenancies) ? data.tenancies : []),
    [data]
  );

  // Which tenancies the response flags somewhere — used by the "Hinweise" filter and
  // the row marker. Derived from the warning groups' own ids, never invented.
  const flaggedIds = useMemo(() => flaggedTenancyIds(data?.warnings), [data]);
  const hasHint = useCallback((row) => rowHasHint(row, flaggedIds), [flaggedIds]);

  const applyFilters = (next) => {
    setFilters(next);
    setOffset(0);
  };
  const selectKpi = (kpi) => applyFilters({ ...filters, kpi });

  // The filtered view. Client-side by design (see the module docstring): the whole
  // response is already here, so narrowing it costs no request.
  const tenancyRows = useMemo(
    () => filterTenancyRows(allTenancies, filters, flaggedIds),
    [allTenancies, filters, flaggedIds]
  );
  const documentRows = useMemo(
    () => countedDocumentRows(tenancyRows, filters),
    [tenancyRows, filters]
  );

  const rows = tab === "documents" ? documentRows : tenancyRows;
  const total = rows.length;
  const pageRows = useMemo(
    () => rows.slice(offset, offset + limit),
    [rows, offset, limit]
  );

  // Offsets survive a tab switch otherwise, leaving the reader on an empty page 4.
  useEffect(() => {
    setOffset(0);
  }, [tab]);

  const kpi = activeKpi(filters);
  const isFiltered =
    Boolean(filters.q || filters.status || filters.hint) || filters.kpi !== "all";
  const pct = coveragePercent(coverage);
  const fresh = data?.freshness;
  const nouns = NOUNS[tab] || NOUNS.tenancies;

  const chips = [
    filters.kpi !== "all" && {
      key: "kpi",
      label: KPI_CHIP_LABEL[filters.kpi] || filters.kpi,
      onRemove: () => applyFilters({ ...filters, kpi: "all" }),
    },
    filters.status && {
      key: "status",
      label: mapLabel(INVOICE_STATUS_LABEL, filters.status),
      onRemove: () => applyFilters({ ...filters, status: "" }),
    },
    filters.hint && {
      key: "hint",
      label: filters.hint === "with" ? "Nur mit Hinweis" : "Ohne Hinweis",
      onRemove: () => applyFilters({ ...filters, hint: "" }),
    },
    filters.q && {
      key: "q",
      label: `Suche „${filters.q}“`,
      onRemove: () => applyFilters({ ...filters, q: "" }),
    },
  ];

  return (
    <div className="p-4 sm:p-6">
      <header className="mb-4 flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <h1 className="text-xl font-semibold text-foreground">
            Bexio Zahlungsabgleich
          </h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Schreibgeschützter Abgleich der Bexio-Rechnungen mit offenen, bezahlten
            und zu prüfenden Positionen.
          </p>
        </div>

        {/* Data currency, top right as in the reference. NOT a sync control: this is
            a mirror, so the honest statement is when it was last filled — and the
            staleness verdict plus its threshold are the backend's own, not a limit
            invented here. */}
        <div className="flex items-center gap-3">
          <div className="text-right">
            <div className="text-xs text-muted-foreground">
              {fresh?.last_successful_invoices_sync
                ? "Letzter erfolgreicher Rechnungs-Sync"
                : "Rechnungs-Sync"}
            </div>
            <div className="text-sm font-medium text-foreground">
              {fresh?.last_successful_invoices_sync
                ? formatDateTime(fresh.last_successful_invoices_sync)
                : "Noch kein erfolgreicher Sync"}
            </div>
            {fresh?.last_successful_invoices_sync ? (
              <div className="text-xs text-muted-foreground">
                {relativeFromNow(fresh.last_successful_invoices_sync) || DASH}
              </div>
            ) : null}
          </div>
          {fresh?.is_stale ? (
            <span className="whitespace-nowrap rounded-full border border-amber-200 bg-amber-50 px-2.5 py-0.5 text-xs font-medium text-amber-800 dark:border-amber-500/30 dark:bg-amber-500/10 dark:text-amber-300">
              Daten älter als {fresh.threshold_hours} h
            </span>
          ) : fresh ? (
            <span className="whitespace-nowrap rounded-full border border-emerald-200 bg-emerald-50 px-2.5 py-0.5 text-xs font-medium text-emerald-700 dark:border-emerald-500/30 dark:bg-emerald-500/10 dark:text-emerald-400">
              Aktuell
            </span>
          ) : null}
          {/* Re-reads THIS page. It does not ask Bexio for anything. */}
          <button
            type="button"
            onClick={load}
            disabled={loading}
            className="inline-flex items-center gap-1.5 rounded-lg border border-border px-3 py-1.5 text-sm font-medium text-foreground hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-60"
          >
            <RefreshCw aria-hidden="true" className="h-4 w-4" />
            Ansicht aktualisieren
          </button>
        </div>
      </header>

      {error && !data ? (
        <BexioTableError message={error} onRetry={load} />
      ) : (
        <>
          {/* KPI row — org-wide summary figures. Each clickable card selects rows by
              the very field it displays, so a card and the rows it reveals cannot
              describe different sets. */}
          <section
            aria-label="Kennzahlen"
            className="mb-4 grid gap-4 sm:grid-cols-2 xl:grid-cols-5"
          >
            <BexioKpiFilterCard
              icon={Clock}
              tone="bad"
              label="Offen (fällig)"
              value={summary ? fmtMoney(summary.open_due, currency) : DASH}
              hint="Fälligkeitsdatum erreicht oder überschritten"
              active={kpi === "open_due"}
              onSelect={() => selectKpi("open_due")}
            />
            <BexioKpiFilterCard
              icon={Clock}
              tone="warn"
              label="Offen (nicht fällig)"
              value={
                summary
                  ? fmtMoney(summary.open_not_yet_due_or_no_due_date, currency)
                  : DASH
              }
              /* The bucket genuinely holds both cases — the service puts invoices
                 with no due date here too — so the card says both. */
              hint="Noch nicht fällig oder ohne Fälligkeitsdatum"
              active={kpi === "open_not_yet_due"}
              onSelect={() => selectKpi("open_not_yet_due")}
            />
            <BexioKpiFilterCard
              icon={Wallet}
              tone="primary"
              label="Offen gesamt"
              value={summary ? fmtMoney(summary.open_total, currency) : DASH}
              hint="Summe der beiden offenen Beträge"
            />
            <BexioKpiFilterCard
              icon={CheckCircle2}
              tone="good"
              label="Bezahlt"
              /* `paid_total_invoice_level`: invoice-level `total_paid` over the
                 counted documents. Not payment rows, and not a calendar year — the
                 contract has no period, so the label claims none. */
              value={
                summary ? fmtMoney(summary.paid_total_invoice_level, currency) : DASH
              }
              hint="Auf Rechnungsebene, berücksichtigte Rechnungen"
              active={kpi === "paid"}
              onSelect={() => selectKpi("paid")}
            />
            <BexioKpiFilterCard
              icon={FileText}
              tone="neutral"
              label="Fakturiert (berücksichtigt)"
              value={summary ? fmtMoney(summary.invoice_total_counted, currency) : DASH}
              hint="Alle Filter zurücksetzen"
              active={kpi === "all" && !filters.status && !filters.hint}
              onSelect={() => applyFilters({ ...EMPTY_FILTERS, q: filters.q })}
              selectHint="Filter zurücksetzen"
            />
          </section>

          <div className="mb-4">
            <BexioFilterBar
              searchValue={filters.q}
              onSearchChange={(q) => applyFilters({ ...filters, q })}
              searchPlaceholder="Mietverhältnis, Mieter, Objekt oder Beleg-Nr. suchen …"
              searchLabel="Abgleich durchsuchen"
              searchHint="Suche und Filter wirken auf die geladenen Abgleich-Daten; die Kennzahlen oben bleiben org-weit."
              onReset={() => applyFilters(EMPTY_FILTERS)}
              canReset={isFiltered}
              resultLabel={`${total} ${total === 1 ? nouns.one : nouns.many}`}
            >
              <BexioFilterSelect
                label="Bexio Status"
                value={filters.status}
                onChange={(status) => applyFilters({ ...filters, status })}
                options={STATUS_OPTIONS}
              />
              <BexioFilterSelect
                label="Hinweise"
                value={filters.hint}
                onChange={(hint) => applyFilters({ ...filters, hint })}
                options={HINT_OPTIONS}
              />
            </BexioFilterBar>
          </div>

          <div className="mb-3">
            <BexioActiveFilters
              chips={chips}
              onClearAll={() => applyFilters(EMPTY_FILTERS)}
            />
          </div>

          {/* Org-wide analysis. All three read the full response, which the heading
              states so a narrowed table below cannot be mistaken for their basis. */}
          <section
            aria-label="Org-weite Analyse"
            className="mb-4 grid gap-4 lg:grid-cols-3"
          >
            <BexioTopOpenCard
              tenancies={allTenancies}
              currency={currency}
              onSelect={setSelected}
            />
            <BexioMappingCoverageCard coverage={coverage} currency={currency} />
            <BexioStatusMixCard statusCounts={data?.status_counts} />
          </section>

          {/* "Zu prüfen": the seven warning groups with their own counts and a
              per-group drilldown. Each count is that group's own length — nothing is
              added up, so `invalid_mapped_or_unresolved` (a sub-count of
              `mapped_quad`) cannot inflate a total here. */}
          <div className="mb-4">
            <BexioWarningsCard reconciliation={data} />
          </div>

          <section
            aria-label="Abgleich"
            className="overflow-hidden rounded-xl border border-border bg-card"
          >
            <div
              role="tablist"
              aria-label="Ansicht"
              className="flex gap-1 overflow-x-auto border-b border-border px-2 pt-2"
            >
              {TABS.map((t) => {
                /* Each badge counts the rows ITS OWN tab renders, so it can never
                   disagree with the list underneath. Summing the rows'
                   `counted_invoices` was wrong: under a status filter a tenancy
                   stays because ONE of its invoices matches, and the badge then
                   claimed all of them — 2 above a list of 1. */
                const count =
                  t.key === "documents" ? documentRows.length : tenancyRows.length;
                return (
                  <button
                    key={t.key}
                    type="button"
                    role="tab"
                    aria-selected={tab === t.key}
                    onClick={() => setTab(t.key)}
                    className={`whitespace-nowrap rounded-t-lg border-b-2 px-3 py-2 text-sm font-medium focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring ${
                      tab === t.key
                        ? "border-primary text-foreground"
                        : "border-transparent text-muted-foreground hover:text-foreground"
                    }`}
                  >
                    {t.label}
                    <span className="ml-1.5 text-xs text-muted-foreground">
                      {count}
                    </span>
                  </button>
                );
              })}
            </div>

            {loading && !data ? (
              <BexioTableSkeleton columns={8} />
            ) : total === 0 ? (
              <BexioTableEmpty
                filtered={isFiltered}
                noun={nouns.many}
                onReset={() => applyFilters(EMPTY_FILTERS)}
              />
            ) : (
              <>
                {tab === "documents" ? (
                  <BexioCountedDocumentsTable
                    rows={pageRows}
                    currency={currency}
                    onSelect={(d) =>
                      setSelected(
                        allTenancies.find((t) => t.tenancy_id === d.tenancy_id) || null
                      )
                    }
                  />
                ) : (
                  <BexioTenancyTable
                    rows={pageRows}
                    currency={currency}
                    hasHint={hasHint}
                    onSelect={setSelected}
                  />
                )}
                <BexioTablePagination
                  total={total}
                  offset={offset}
                  limit={limit}
                  onOffsetChange={setOffset}
                  onLimitChange={(next) => {
                    setLimit(next);
                    setOffset(0);
                  }}
                  noun={nouns.dative}
                  busy={loading}
                />
              </>
            )}
          </section>
        </>
      )}

      <BexioDetailDrawer
        open={selected != null}
        onOpenChange={(next) => {
          if (!next) setSelected(null);
        }}
        title={
          selected
            ? selected.tenancy_reference_number ||
              selected.tenant_display_name ||
              `Mietverhältnis ${selected.tenancy_id}`
            : ""
        }
        subtitle={selected?.tenant_display_name || undefined}
      >
        {selected ? (
          <BexioReconciliationDrawerBody
            row={selected}
            currency={currency}
            hasHint={hasHint}
            isFlagged={(r) => flaggedIds.has(String(r.tenancy_id))}
            coveragePct={pct}
          />
        ) : null}
      </BexioDetailDrawer>

      {/* A refresh that failed while data is already on screen must not blank the
          page — the stale-but-real view stays and the failure is stated. */}
      {error && data ? (
        <div className="mt-4">
          <BexioTableError message={error} onRetry={load} />
        </div>
      ) : null}
    </div>
  );
}
